(() => {
  'use strict';
  if (globalThis.JaxBulkEligibility) return;

  const minimumQuantity = 10;
  const maximumQuantity = 999;
  const ordinaryMaximumQuantity = 99;
  const properties = Object.freeze({
    '_Jax bulk eligible': 'repeated-design-v1',
    '_Jax bulk source': 'bulk-builder-v1'
  });
  const ids = /^[1-9]\d{0,23}$/;
  const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
  const identifier = value => typeof value === 'string' && value.length > 0 && value.length <= 80;
  const types = new Set(['image/png', 'image/jpeg', 'image/svg+xml', 'application/pdf']);
  const empty = Object.freeze({});
  const rejected = reason => ({ eligible: false, reason, maxQuantity: ordinaryMaximumQuantity, properties: empty });

  // Classify the current editable document, never a previously claimed flag.
  // These ordinary Shopify line properties are buyer-controlled metadata, not
  // a server-issued proof. Checkout must separately check the configured
  // variant allowlist and this line's quantity; this does not activate a discount.
  function classify({ document, eligibleVariantIds, bulkSource } = {}) {
    if (bulkSource !== 'bulk-builder-v1') return rejected('source');
    if (!object(document) || document.kind !== 'engraving-studio' || document.version !== 1 ||
        !ids.test(document.productId || '') || !ids.test(document.variantId || '') ||
        typeof document.productId !== 'string' || typeof document.variantId !== 'string' ||
        Object.hasOwn(document, 'rows') || Object.hasOwn(document, 'base')) return rejected('document');
    if (!Array.isArray(eligibleVariantIds) || !eligibleVariantIds.some(id => typeof id === 'string' && id === document.variantId)) return rejected('variant');

    const sides = document.sides;
    if (!object(sides) || Object.keys(sides).some(side => side !== 'front' && side !== 'back') ||
        !Array.isArray(sides.front) || !Array.isArray(sides.back)) return rejected('document');
    if (sides.front.length && sides.back.length) return rejected('sides');
    const side = sides.front.length ? 'front' : 'back';
    if (!sides[side].length || sides[side].length > 8) return rejected('artwork');
    if (!Array.isArray(document.assets) || document.assets.length > 16) return rejected('asset');
    const assets = new Map(), used = new Set(), layers = new Set();
    for (const asset of document.assets) {
      if (!object(asset) || !identifier(asset.id) || assets.has(asset.id) || !types.has(asset.mime) ||
          typeof asset.original !== 'string' || !asset.original.startsWith(`data:${asset.mime};base64,`) ||
          asset.original.length <= `data:${asset.mime};base64,`.length) return rejected('asset');
      assets.set(asset.id, asset);
    }
    for (const layer of sides[side]) {
      if (!object(layer) || !['logo', 'text', 'monogram'].includes(layer.type) || !identifier(layer.id) || layers.has(layer.id)) return rejected('artwork');
      layers.add(layer.id);
      if (!['x', 'y', 'size', 'rotation'].every(key => typeof layer[key] === 'number' && Number.isFinite(layer[key])) ||
          layer.x < 0 || layer.x > 100 || layer.y < 0 || layer.y > 100 || layer.size <= 0 || layer.size > 10001 ||
          layer.rotation < -180 || layer.rotation > 180) return rejected('artwork');
      if (layer.type === 'logo') {
        if (!assets.has(layer.assetId) || Object.hasOwn(layer, 'text') || Object.hasOwn(layer, 'font')) return rejected('artwork');
        used.add(layer.assetId);
      } else if (typeof layer.text !== 'string' || !layer.text.trim() || Array.from(layer.text).length > (layer.type === 'monogram' ? 3 : 240) ||
          typeof layer.font !== 'string' || !layer.font || layer.font.length > 100 || Object.hasOwn(layer, 'assetId')) return rejected('artwork');
    }
    if (used.size !== assets.size) return rejected('asset');

    return { eligible: true, reason: 'repeated-design', maxQuantity: maximumQuantity,
      variantId: document.variantId, side, properties };
  }

  const stable = value => {
    if (Array.isArray(value)) return value.map(stable);
    if (object(value)) return Object.fromEntries(Object.keys(value).sort().map(key => [key, stable(value[key])]));
    if (typeof value === 'number' && !Number.isFinite(value)) throw new Error('bulk_design_invalid');
    return value;
  };
  const hash = async value => Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))), byte => byte.toString(16).padStart(2, '0')).join('');
  async function designKey(document) {
    const result = classify({ document, eligibleVariantIds: [document?.variantId], bulkSource: document?.bulkSource });
    if (!result.eligible) return null;
    const assets = new Map();
    for (const asset of document.assets) assets.set(asset.id, {
      mime: asset.mime, original: await hash(asset.original), options: stable(asset.options || {})
    });
    const layers = document.sides[result.side].map(layer => {
      const clean = Object.fromEntries(Object.entries(layer).filter(([key]) => !['id', 'assetId', 'groupId', 'preparationRequested'].includes(key)));
      if (layer.type === 'logo') clean.artwork = assets.get(layer.assetId);
      return stable(clean);
    });
    // Product/color IDs, random layer IDs, filenames and proof/calibration URLs
    // do not change the engraving. Text, font, side, layer order, geometry,
    // original bytes and artwork-preparation options do change its identity.
    return `sha256:${await hash(JSON.stringify({ version: 1, side: result.side, layers }))}`;
  }

  function quantityAllowed({ document, eligibleVariantIds, bulkSource, quantity } = {}) {
    return Number.isInteger(quantity) && quantity >= 1 &&
      quantity <= classify({ document, eligibleVariantIds, bulkSource }).maxQuantity;
  }

  globalThis.JaxBulkEligibility = Object.freeze({
    minimumQuantity, maximumQuantity, ordinaryMaximumQuantity, properties, classify, quantityAllowed, designKey
  });
})();
